import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedDialog from '../../../../shared/shared-dialog';
import { handleErrors } from '../../../../utils/errors';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { useStore } from '../../../state/zustand-store';
import {
  updateApiIpIntrusionStatus,
  UserKeys,
} from '../../../../api/users-api';
import { IP_Settings } from '../../../../../types';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface ToggleApiIpIntrusionDialogProps {
  open: boolean;
  handleClose: () => void;
  model: IP_Settings;
  changeLogRoles: string[];
}

export default function ToggleApiIpIntrusionDialog({
  open,
  handleClose,
  model,
  changeLogRoles,
}: ToggleApiIpIntrusionDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateApiIpIntrusionStatus(!model.apiIpRestricting, changeLogMessage),
    onSuccess: (updateIpSettings) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: model.apiIpRestricting
          ? 'Disabled API IP Intrusion successfully!'
          : 'Enabled API IP Intrusion successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.IpIntrusionSettings],
        updateIpSettings
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: model.apiIpRestricting
          ? 'There was an issue disabling API IP Intrusion. Please refresh and try again.'
          : 'There was an issue enabling API IP Intrusion. Please refresh and try again.',
      });
    },
    onSettled: () => {
      const event = model.apiIpRestricting
        ? analytics.administration.settings.disable_api_ip_intrusion
        : analytics.administration.settings.enable_api_ip_intrusion;

      createEvent(event, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.updateApiIpIntrusionStatus.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        model.apiIpRestricting
          ? 'Disable API IP Intrusion'
          : 'Enable API IP Intrusion'
      }
      dialogContent={
        <Box>
          <Typography>
            {model.apiIpRestricting
              ? 'Do you wish to disable API IP Intrusion?'
              : `Do you wish to enable API IP Intrusion? This will only impact gPanel API requests and not the web application.`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: model.apiIpRestricting
          ? 'Disable API IP Intrusion'
          : 'Enable API IP Intrusion',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
